import { Injectable } from "@angular/core";
import { DictionaryService } from "@app/shared/services/dictionary/dictionary.service";
import { HttpClient } from "@angular/common/http";
import { VehicleType } from "@app/calculate/vehicle/vehicle.model";
import { Observable, of } from "rxjs";
import { DictionaryItem } from "@app/shared/services/dictionary/dictionary-item.model";
import { CalculationStoreService } from "@app/calculate/calculate/calculation-store.service";
import { VehicleModelModel } from "@app/calculate/vehicle/vehicle-model.model";
import { EngineCapacity } from "@app/calculate/vehicle/engine-capacity.model";
import { GetVehicleModel } from "@app/calculate/vehicle/get-vehicle-model.model";
import { Cities } from "@app/shared/models/address/Cities";
import { map } from "rxjs/operators";
import { Streets } from "@app/shared/models/address/Streets";
import { RequestStoreService } from "@app/shared/services/request-store/request-store.service";
import { RequestStoreKey } from "@app/shared/services/request-store/request-store-key.model";

@Injectable()
export class CalculateDictionaryService extends DictionaryService {
  constructor(
    httpClient: HttpClient,
    private requestStoreService: RequestStoreService,
    private storeService: CalculationStoreService
  ) {
    super(httpClient);
  }

  getBrands(
    productionYear: number,
    vehicleType: VehicleType = null
  ): Observable<DictionaryItem[]> {
    const params = vehicleType
      ? { productionYear, vehicleType }
      : { productionYear };
    const storeRequestBrands = this.requestStoreService.getRequest(
      RequestStoreKey.VEHICLE_BRANDS
    );

    if (
      storeRequestBrands &&
      storeRequestBrands.body &&
      storeRequestBrands.params.productionYear === productionYear &&
      storeRequestBrands.params.type === vehicleType
    ) {
      return of(storeRequestBrands.body);
    } else {
      return this.getDictionary<DictionaryItem>(`/vehicles/brands`, params);
    }
  }

  getModels(
    productionYear: number,
    brandId: string,
    vehicleType: VehicleType = null
  ): Observable<VehicleModelModel[]> {
    const params = { productionYear, brandId };
    const storeRequestModels = this.requestStoreService.getRequest(
      RequestStoreKey.VEHICLE_MODELS
    );

    if (
      storeRequestModels &&
      storeRequestModels.body &&
      storeRequestModels.params.productionYear === productionYear &&
      storeRequestModels.params.type === vehicleType &&
      storeRequestModels.params.brandId === brandId
    ) {
      return of(storeRequestModels.body);
    } else {
      return this.getDictionary<VehicleModelModel>(
        `/vehicles/models`,
        vehicleType ? { ...params, vehicleType } : params
      );
    }
  }

  getModelsByRegistrationNumber(
    calculationId: string,
    registrationNumber: string
  ): Observable<VehicleModelModel[]> {
    return this.getDictionary<VehicleModelModel>(
      `/vehicles/models/${calculationId}`,
      { registrationNumber }
    );
  }

  getVehicle(
    calculationId: string,
    registrationNumber: string,
    productionYear: number
  ): Observable<GetVehicleModel> {
    return this.getDictionaryItem<GetVehicleModel>(
      `/vehicles/${calculationId}`,
      { registrationNumber, productionYear }
    );
  }

  getCities(postalCode: string): Observable<Cities> {
    return this.getDictionary<DictionaryItem>(`/cities/${postalCode}`).pipe(
      map((cities) => Cities.of(cities))
    );
  }

  getStreets(postalCode: string, city: DictionaryItem): Observable<Streets> {
    return this.getDictionary<string>(
      `/streets/${postalCode}/${city.key}`
    ).pipe(map((streets) => Streets.of(streets)));
  }

  getVehicleAverageYearlyMileages(): Observable<DictionaryItem[]> {
    return Observable.create((observer) =>
      observer.next(
        this.storeService.getCalculationDictionary()
          .vehicleAverageYearlyMileages
      )
    );
  }

  getVehicleAbroadUsageTimes(): Observable<DictionaryItem[]> {
    return Observable.create((observer) =>
      observer.next(
        this.storeService.getCalculationDictionary().vehicleAbroadUsageTimes
      )
    );
  }

  getVehicleUsages(): Observable<DictionaryItem[]> {
    return Observable.create((observer) =>
      observer.next(this.storeService.getCalculationDictionary().vehicleUsages)
    );
  }

  getParkingPlaces(): Observable<DictionaryItem[]> {
    return Observable.create((observer) =>
      observer.next(this.storeService.getCalculationDictionary().parkingPlaces)
    );
  }

  getYoungestDriverAge(): Observable<DictionaryItem[]> {
    return Observable.create((observer) =>
      observer.next(
        this.storeService.getCalculationDictionary().youngestDriverAge
      )
    );
  }

  getInsuredInPolandFromYear(): Observable<DictionaryItem[]> {
    return Observable.create((observer) =>
      observer.next(
        this.storeService.getCalculationDictionary().insuredInPolandFromYear
      )
    );
  }

  getOcDamageCountForLastSixYears(): Observable<DictionaryItem[]> {
    return Observable.create((observer) =>
      observer.next(
        this.storeService.getCalculationDictionary().tplClaimsCountLastSixYears
      )
    );
  }

  getOcDamageCountForLastThreeYears(): Observable<DictionaryItem[]> {
    return Observable.create((observer) =>
      observer.next(
        this.storeService.getCalculationDictionary()
          .tplClaimsCountLastThreeYears
      )
    );
  }

  getOcInsuranceYears(): Observable<DictionaryItem[]> {
    return Observable.create((observer) =>
      observer.next(
        this.storeService.getCalculationDictionary().tplInsuranceYearsCount
      )
    );
  }
  getOcDamageCountForLastYear(): Observable<DictionaryItem[]> {
    return Observable.create((observer) =>
      observer.next(
        this.storeService.getCalculationDictionary().tplClaimsCountLastYear
      )
    );
  }

  getAcInsuranceYears(): Observable<DictionaryItem[]> {
    return Observable.create((observer) =>
      observer.next(
        this.storeService.getCalculationDictionary().acInsuranceYearsCount
      )
    );
  }

  getAcDamageCountForLastSixYears(): Observable<DictionaryItem[]> {
    return Observable.create((observer) =>
      observer.next(
        this.storeService.getCalculationDictionary().acClaimsCountLastSixYears
      )
    );
  }

  getAcDamageCountForLastThreeYears(): Observable<DictionaryItem[]> {
    return Observable.create((observer) =>
      observer.next(
        this.storeService.getCalculationDictionary().acClaimsCountLastThreeYears
      )
    );
  }

  getAcDamageCountForLastYear(): Observable<DictionaryItem[]> {
    return Observable.create((observer) =>
      observer.next(
        this.storeService.getCalculationDictionary().acClaimsCountLastYear
      )
    );
  }

  getInstallments(): Observable<DictionaryItem[]> {
    return Observable.create((observer) =>
      observer.next(this.storeService.getCalculationDictionary().installments)
    );
  }

  convertEngineCapacityToDictionaryItem(
    engineCapacity: EngineCapacity
  ): DictionaryItem {
    return new DictionaryItem(
      `${engineCapacity.min}_${engineCapacity.max}`,
      engineCapacity.label,
      null,
      engineCapacity
    );
  }
}
